import { IEvent } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Button, message } from 'antd';
import Lottie from 'lottie-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Wrapper, InfoWrapper, TitleWrapper, TimeWrapper } from './OngoingEventItem.styles';

import LIVE_EVENT from '@/assets/lotties/live_event.json';
import { useMutateEvents, useProfile } from '@/store';

const LOTTIE_SIZE = '16px';

type Props = {
  event: IEvent;
};

export const OngoingEventItem: React.FC<Props> = ({ event }) => {
  const { t } = useTranslation('CommonDashboardTranslations');
  const { addAttendee, isWorking } = useMutateEvents();
  const { profile } = useProfile();
  const navigateTo = useNavigate();

  const now = dateProvider.now();

  const isStarted = useMemo(() => {
    return dateProvider.isLive(event.startDate, event.endDate);
  }, [event]);

  const timeStartedTitle = useMemo(() => {
    const time = dateProvider.diff(now, dateProvider.zonedTimeToUtc(event.startDate), 'minutes');
    if (isStarted) {
      return t('CLASS_STARTED_AT', { time });
    }
    return t('CLASS_STARTING_IN', { time });
  }, [isStarted, event, now]);

  const isUserAttending = useMemo(() => {
    if (!profile) return false;
    return event.attendees.some(attendee => attendee._id === profile._id);
  }, [profile, event]);

  const goToEvent = () => {
    navigateTo(`/content/calendar/${event._id}`);
  };

  const handleJoinEvent = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!profile) return;
    if (!isUserAttending) addAttendee({ eventId: event._id, attendeeId: profile._id });
    if (!event.conferenceData?.meetingId) {
      message.error(t('NO_LIVE_CLASS_LINK'));
      return;
    }

    navigateTo(`/live-class/${event.conferenceData.meetingId}`);
  };

  return (
    <Wrapper onClick={goToEvent}>
      <InfoWrapper>
        <TitleWrapper>{event.title}</TitleWrapper>
        <TimeWrapper>{timeStartedTitle}</TimeWrapper>
      </InfoWrapper>
      <Button
        type="primary"
        loading={isWorking}
        onClick={handleJoinEvent}
        icon={
          <div style={{ width: LOTTIE_SIZE, height: LOTTIE_SIZE }}>
            <Lottie animationData={LIVE_EVENT} />
          </div>
        }>
        {t('JOIN_CLASS')}
      </Button>
    </Wrapper>
  );
};
